import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { StylesConfig } from 'react-select';
import styles from './shared.module.css';
import { PageFrame, PageHeader, FilterBar, Button, Icon, Pager } from '@/components/ui';
import type { StatItem } from '@/components/ui';
import { GroupHeaderCell, layoutHeader, useGroupHeaderRow } from './groupedHeader';
import type { ColumnGroup } from './groupedHeader';

export type { ColumnGroup };

type SelectOption = { value: string; label: string };

export const selectStyles: StylesConfig<SelectOption, false> = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};

interface HeaderLabelProps {
  header: React.ReactNode;
  tooltip?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /** Whether this column is the one the data is sorted by. */
  active?: boolean;
  sortDir?: 'asc' | 'desc';
}

/**
 * What goes inside a sortable header cell: the label, an ⓘ tooltip mark and
 * the sort indicator. In a right-aligned column the icon and the mark go
 * *before* the label, so the label's last character lines up with the
 * figures. DataTable uses it; a hand-built table (Trade Suggestions) uses it
 * inside its own <th> so both heads look and read the same.
 */
export const HeaderLabel: React.FC<HeaderLabelProps> = ({ header, tooltip, align, sortable, active, sortDir }) => {
  const leading = align === 'right';
  const tooltipMark = tooltip && (
    <Tooltip title={tooltip} arrow placement="top">
      <span className={`${styles['th-tip']}${leading ? ` ${styles['th-tip-leading']}` : ''}`}>ⓘ</span>
    </Tooltip>
  );
  // A sortable column shows a faint up/down pair on hover; the sorted one
  // shows the single direction the data is actually in.
  const sortIcon = sortable && (
    <span className={`${styles['sort-icon']}${leading ? ` ${styles['sort-icon-leading']}` : ''}`}>
      <Icon name={!active ? 'sortable' : sortDir === 'asc' ? 'sortAsc' : 'sortDesc'} size={13} />
    </span>
  );
  return (
    <>
      {leading && sortIcon}
      {leading && tooltipMark}
      {header}
      {!leading && tooltipMark}
      {!leading && sortIcon}
    </>
  );
};

export interface ColumnDef<T> {
  key: string;
  header: string;
  tooltip?: string;
  sortable?: boolean;
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
  cellClassName?: (row: T) => string;
  /** When any column defines this, a totals <tfoot> row is rendered.
   *  `rows` is the table's `footerRows`, falling back to the current page. */
  renderFooter?: (rows: T[]) => React.ReactNode;
  /** Key of a `columnGroups` entry. Consecutive columns with the same group sit
   *  under one centred header; a column with no group spans both header rows.
   *  Keep grouped columns out of the `stickyColumns` range. */
  group?: string;
}

interface DataTableProps<T> {
  title?: string;
  data: T[];
  columns: ColumnDef<T>[];
  /** Labels (and optional tooltips) for the `group` keys used in `columns`.
   *  A group key with no entry here is shown as its own label. */
  columnGroups?: ColumnGroup[];
  rowKey: (row: T, index: number) => string | number;
  rowStyle?: (row: T) => React.CSSProperties | undefined;
  totalCount: number;
  loading: boolean;
  error?: string | null;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  hideSearch?: boolean;
  filterSlot?: React.ReactNode;
  footerSlot?: React.ReactNode;
  toolbarSlot?: React.ReactNode;
  /** Rows the `renderFooter` totals are computed over; defaults to `data`. A paginated
   *  table must pass its full filtered set, or the totals describe one page only. */
  footerRows?: T[];
  searchRowSlot?: React.ReactNode;
  hasActiveFilters?: boolean;
  /** Page sizes the reader can choose from. Renders a control in the footer
   *  beside the count; omit it and the footer just shows the pager. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** How many filters are applied — shown as a badge on the Filters button.
   *  Falls back to 1/0 from `hasActiveFilters` when a page has not counted. */
  activeFilterCount?: number;
  onClearFilters?: () => void;
  onDownloadCSV?: () => void;
  isDownloading?: boolean;
  /** Rendered next to the title, e.g. a page-level "Upload" button — not tied to filters. */
  headerActions?: React.ReactNode;
  /** Mono caption above the title — the section or data source. */
  eyebrow?: React.ReactNode;
  /** One line under the title: what this page shows, as-on dates, caveats. */
  subtitle?: React.ReactNode;
  /** Figures worth knowing before reading the table. Rendered as tiles on the
   *  band. Keep to five; format them with @/utils/formatters. */
  stats?: StatItem[];
  emptyMessage?: string;
  loadingMessage?: string;
  onRowClick?: (row: T) => void;
  compact?: boolean;
  stickyColumns?: number;
  /** Hide the count-and-pager footer. Only for a static reference table that never
   *  pages — "Showing 1–5 of 5" under every card is noise, not information. */
  hideFooter?: boolean;
  /** When true, the card shrinks to its content instead of the default min-height: 80vh.
   *  Avoids large whitespace (and a floating totals row) below short tables.
   *  No effect when `embedded` is set, since there is no outer card to size. */
  fitContent?: boolean;
  /** When true, renders without the outer page/card wrapper so several tables can be
   *  composed inside a single shared card. `title` still renders as a section header.
   *  Note: `fitContent` is ignored in this mode — the parent controls sizing of its own card. */
  embedded?: boolean;
  /** Caps the scroll area's height (any CSS length); rows beyond it scroll under
   *  the sticky header. Mainly for `embedded` tables, which otherwise grow to fit. */
  maxBodyHeight?: string;
}

function DataTable<T,>({
  title,
  data,
  columns,
  columnGroups,
  rowKey,
  rowStyle,
  totalCount,
  loading,
  error,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  sortField,
  sortDir,
  onSort,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  hideSearch = false,
  filterSlot,
  toolbarSlot,
  searchRowSlot,
  hasActiveFilters,
  pageSizeOptions,
  onPageSizeChange,
  activeFilterCount,
  onClearFilters,
  onDownloadCSV,
  isDownloading,
  headerActions,
  eyebrow,
  subtitle,
  stats,
  footerSlot,
  footerRows,
  emptyMessage = 'No data found.',
  loadingMessage = 'Loading…',
  onRowClick,
  compact = false,
  hideFooter = false,
  stickyColumns,
  fitContent = false,
  embedded = false,
  maxBodyHeight,
}: DataTableProps<T>) {
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + data.length - 1;

  // Precompute cumulative left offsets for sticky columns (derived from col.width).
  // Only runs when stickyColumns is set; offsets[i] = sum of widths of cols 0..i-1.
  const stickyLeftOffsets = useMemo<number[]>(() => {
    if (!stickyColumns) return [];
    const offsets: number[] = [];
    let acc = 0;
    for (let i = 0; i < stickyColumns; i++) {
      offsets.push(acc);
      const w = columns[i]?.width;
      acc += w ? parseFloat(w) : 0;
    }
    return offsets;
  }, [stickyColumns, columns]);

  const headerLayout = useMemo(() => layoutHeader(columns, columnGroups), [columns, columnGroups]);
  const { tableRef, groupRowRef } = useGroupHeaderRow(headerLayout.grouped);

  /** One leaf header cell. In a grouped head, `row` says whether it spans both
   *  rows (an ungrouped column) or sits in the leaf row under its group. */
  const renderHeaderCell = (col: ColumnDef<T>, colIndex: number, row?: 'span' | 'sub') => {
    const isActive = sortField === col.key;
    const alignClass = col.align === 'right'
        ? styles['text-right']
        : col.align === 'center'
            ? styles['text-center']
            : '';
    const thClass = [
      alignClass,
      col.sortable ? styles['th-sortable'] : '',
      col.sortable && isActive ? styles['th-active'] : '',
    ]
      .filter(Boolean)
      .join(' ');

    const isSticky = stickyColumns !== undefined && colIndex < stickyColumns;
    const isLastSticky = stickyColumns !== undefined && colIndex === stickyColumns - 1;
    const thStyle: React.CSSProperties = {};
    if (col.width) thStyle.width = col.width;
    if (stickyColumns !== undefined) {
      if (isSticky) {
        thStyle.position = 'sticky';
        thStyle.top = row === 'sub' ? 'var(--group-head-h, 33px)' : 0;
        thStyle.left = `${stickyLeftOffsets[colIndex]}px`;
        thStyle.zIndex = 4;
        thStyle.backgroundColor = 'var(--tbl-head-bg)';
        if (isLastSticky) thStyle.boxShadow = 'var(--shadow-pin)';
      } else {
        // Suppress shared.module.css z-index:10 on non-sticky headers
        // so they don't slide over the pinned columns.
        thStyle.zIndex = 1;
      }
    }


    return (
      <th
        key={col.key}
        rowSpan={row === 'span' ? 2 : undefined}
        className={thClass || undefined}
        style={Object.keys(thStyle).length > 0 ? thStyle : undefined}
        onClick={col.sortable ? () => onSort(col.key) : undefined}
      >
        <HeaderLabel
          header={col.header}
          tooltip={col.tooltip}
          align={col.align}
          sortable={col.sortable}
          active={isActive}
          sortDir={sortDir}
        />
      </th>
    );
  };

  // A standalone page is: navy band (title + stats + actions) → toolbar card
  // → table card. Embedded tables keep the flat in-card layout, since their
  // parent already provides the surface and nested cards read as clutter.
  const bandActions = (
    <>
      {headerActions}
      {onDownloadCSV && totalCount > 0 && (
        <Button variant="secondary" onBand onClick={onDownloadCSV} disabled={isDownloading}>
          {isDownloading ? 'Downloading…' : 'Download CSV'}
        </Button>
      )}
    </>
  );

  const content = (
    <>
        {embedded && (title || headerActions) && (
          <div className={styles.header}>
            {title && <h1>{title}</h1>}
            <div className={styles['header-actions']}>{bandActions}</div>
          </div>
        )}

        {/* Search plus one Filters control. The page's filter controls are
            handed to the bar and render inside its panel — a page never lays
            out a filter row itself. */}
        {(filterSlot || !hideSearch) && (
          embedded ? (
            <div className={styles.controls}>
              {!hideSearch && (
                <div className={styles['search-box']}>
                  <Icon name="search" size={15} />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={e => onSearchChange?.(e.target.value)}
                  />
                </div>
              )}
              {/* Same meaning as in the standalone bar: a control the page keeps
                  beside the search rather than in a filter block below it. */}
              {searchRowSlot}
              {filterSlot && <div className={styles['filter-row']}>{filterSlot}</div>}
            </div>
          ) : (
            <FilterBar
              searchValue={searchQuery}
              onSearchChange={onSearchChange}
              searchPlaceholder={searchPlaceholder}
              hideSearch={hideSearch}
              filters={filterSlot}
              activeFilterCount={activeFilterCount ?? (hasActiveFilters ? 1 : 0)}
              onClearFilters={onClearFilters}
            >
              {searchRowSlot}
            </FilterBar>
          )
        )}

        {/* Toggles / alerts from the page. Embedded, they become a padded strip
            that lines up with the surrounding card; standalone, they sit on the
            canvas between the filter bar and the table. */}
        {toolbarSlot && (
          embedded
            ? <div className={styles['embedded-toolbar']}>{toolbarSlot}</div>
            : toolbarSlot
        )}

        {/* Error */}
        {error && <p className={styles['error-msg']}>{error}</p>}

        {/* Table */}
        <div
          className={
            embedded
              ? styles['embedded-table']
              : `${styles['table-panel']}${fitContent ? '' : ` ${styles['panel-grow']}`}`
          }
        >
          <div className={styles['table-scroll']} style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}>
            <table
              ref={tableRef}
              className={`${styles.table} ${compact ? styles['table-compact'] : ''}`.trim()}
              style={stickyColumns ? { tableLayout: 'fixed', width: 'max-content' } : undefined}
            >
              <thead>
                {headerLayout.grouped ? (
                  <>
                    <tr ref={groupRowRef}>
                      {headerLayout.top.map(cell => (
                        cell.kind === 'column'
                          ? renderHeaderCell(cell.column, cell.index, 'span')
                          : <GroupHeaderCell key={cell.key} group={cell.group} span={cell.span} />
                      ))}
                    </tr>
                    <tr className={styles['sub-header-row']}>
                      {headerLayout.sub.map(({ column, index }) => renderHeaderCell(column, index, 'sub'))}
                    </tr>
                  </>
                ) : (
                  <tr>
                    {columns.map((col, colIndex) => renderHeaderCell(col, colIndex))}
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className={styles['empty-cell']}>
                      <div className={styles.loading}>
                        <div className={styles.spinner} />
                        {loadingMessage}
                      </div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className={styles['empty-cell']}>
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={rowKey(row, rowIndex)}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={{ ...(onRowClick ? { cursor: 'pointer' } : {}), ...rowStyle?.(row) }}
                    >
                    {columns.map((col, colIndex) => {
                      const customClass = col.cellClassName ? col.cellClassName(row) : '';
                      const alignClass = col.align === 'right'
                          ? styles['text-right']
                          : col.align === 'center'
                              ? styles['text-center']
                              : '';

                      const isSticky = stickyColumns !== undefined && colIndex < stickyColumns;
                      const isLastSticky = stickyColumns !== undefined && colIndex === stickyColumns - 1;
                      const rowBg = rowStyle?.(row)?.backgroundColor;
                      const tdStyle: React.CSSProperties | undefined = isSticky ? {
                        position: 'sticky',
                        left: `${stickyLeftOffsets[colIndex]}px`,
                        zIndex: 2,
                        // Only set an explicit background when the row has a custom colour (warning/inactive).
                        // For plain rows, leave it unset so CSS :hover rules still apply to sticky cells.
                        ...(rowBg ? { backgroundColor: rowBg } : {}),
                        ...(isLastSticky ? { boxShadow: 'var(--shadow-pin)' } : {}),
                      } : undefined;

                      return (
                        <td
                          key={col.key}
                          className={`${alignClass} ${customClass}`.trim() || undefined}
                          style={tdStyle}
                        >
                          {col.render(row)}
                        </td>
                      );
                    })}
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && data.length > 0 && columns.some(col => col.renderFooter) && (
                <tfoot>
                  <tr>
                    {columns.map((col, colIndex) => {
                      const alignClass = col.align === 'right'
                        ? styles['text-right']
                        : col.align === 'center'
                          ? styles['text-center']
                          : '';

                      const isSticky = stickyColumns !== undefined && colIndex < stickyColumns;
                      const isLastSticky = stickyColumns !== undefined && colIndex === stickyColumns - 1;
                      const tdStyle: React.CSSProperties | undefined = isSticky ? {
                        // bottom: 0 comes from the CSS class; position is repeated so the
                        // left offset does not depend on it.
                        position: 'sticky',
                        left: `${stickyLeftOffsets[colIndex]}px`,
                        // Above the sticky body cells and the other footer cells.
                        zIndex: 10,
                        ...(isLastSticky ? { boxShadow: 'var(--shadow-pin)' } : {}),
                      } : undefined;

                      return (
                        <td key={col.key} className={alignClass || undefined} style={tdStyle}>
                          {col.renderFooter ? col.renderFooter(footerRows ?? data) : null}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

        {/* The row count belongs here, next to the paging numbers — not up in
            the toolbar and not in the page title. Always rendered, so the card
            keeps its footer whether there is one page or fifty. */}
        {!loading && !hideFooter && (
          <div className={styles['table-foot']}>
            <span className={styles['count-info']}>
              {totalCount > 0 ? (
                <>
                  Showing <strong>{rangeStart}–{rangeEnd}</strong> of <strong>{totalCount}</strong>
                </>
              ) : (
                'No records'
              )}
            </span>
            <Pager
              page={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        )}
        </div>

        {/* Footer */}
        {footerSlot && (
          <div style={{ marginTop: '20px' }}>
            {footerSlot}
          </div>
        )}
    </>
  );

  if (embedded) return content;

  return (
    <PageFrame
      header={
        <PageHeader
          title={title ?? ''}
          eyebrow={eyebrow}
          subtitle={subtitle}
          stats={stats}
          actions={(headerActions || (onDownloadCSV && totalCount > 0)) ? bandActions : undefined}
        />
      }
    >
      {content}
    </PageFrame>
  );
}

export default DataTable;
